import * as Sentry from '@sentry/nuxt'
import { useQueryClient } from '@tanstack/vue-query'
import { useClient } from '~/composables/api/supabase/client'
import { useGetJwt } from '~/composables/api/supabase/get-jwt'
import { BACK_TO_DASHBOARD_ID, IMPERSONATING } from '~/utils/consts/cookie-keys'

const SESSION_REFRESH_THRESHOLD_SECONDS = 120

const OFFICE_ROUTE_NAMES = new Set<PropertyKey>([
   'dashboard-id-locations-by-number',
   'dashboard-id-management-sections',
   'dashboard-id-specie',
   'dashboard-id-users',
])

export default defineNuxtRouteMiddleware(async (to, from) => {
   const localePath = useLocalePath()
   const routeBaseName = useRouteBaseName()
   const jwt = useGetJwt()
   const session = useSupabaseSession()
   const impersonating = useCookie<boolean>(IMPERSONATING)
   const backToDashboardID = useCookie<string>(BACK_TO_DASHBOARD_ID)
   const qc = useQueryClient()
   const client = useClient()
   const runtimeConfig = useRuntimeConfig()

   const targetOrgId = to.params.id as string

   const report = (e: unknown, reason: string) => {
      if (runtimeConfig.public.mode === 'development') {
         console.error(`[middleware/dashboard] ${reason}`, e)
         return
      }
      Sentry.captureException(e, { extra: { reason, targetOrgId, path: to.path } })
   }

   const clearImpersonation = () => {
      impersonating.value = false
      backToDashboardID.value = ''
   }

   const toOrganizationPicker = () => {
      clearImpersonation()
      return navigateTo(localePath('/auth/redirect'), { redirectCode: 302 })
   }

   const expiresAt = session.value?.expires_at
   const isFresh =
      !!expiresAt && expiresAt - Math.floor(Date.now() / 1000) > SESSION_REFRESH_THRESHOLD_SECONDS
   const isSameOrganization = !!targetOrgId && targetOrgId === (from.params.id as string)

   if (!isFresh || !isSameOrganization) {
      try {
         await client.auth.refreshSession()
      } catch (e) {
         report(e, 'session-refresh-failed')
         clearImpersonation()
         return navigateTo(localePath('/auth/sign-in'), { redirectCode: 302 })
      }
   }

   const organizationDetails = jwt.value?.organization_details
   if (!organizationDetails) {
      return toOrganizationPicker()
   }

   const targetOrg = organizationDetails.find((org) => org.organization_id === targetOrgId)
   const orgType = targetOrg?.organization_type

   if (targetOrg) {
      clearImpersonation()
   } else {
      let accessibleOrgIds: string[] = []
      try {
         const cachedData = qc.getQueryData<{ organization_id: string | null }[]>([
            'accessible-organizations',
            { enabled: true },
         ])
         if (cachedData) {
            accessibleOrgIds = cachedData.flatMap((o) =>
               o.organization_id ? [o.organization_id] : []
            )
         } else {
            const { data } = await client
               .schema('dashboard')
               .from('accessible_organizations')
               .select('organization_id')
               .throwOnError()
            accessibleOrgIds = (data ?? []).flatMap((o) =>
               o.organization_id ? [o.organization_id] : []
            )
         }
      } catch (e) {
         report(e, 'accessible-organizations-lookup-failed')
         return toOrganizationPicker()
      }

      const canImpersonate =
         accessibleOrgIds.includes(targetOrgId) || !!jwt.value?.user_details?.is_in_office
      if (!canImpersonate) {
         return toOrganizationPicker()
      }

      impersonating.value = true
      if (!backToDashboardID.value) {
         backToDashboardID.value = from.params.id as string
      }
   }

   const baseName = routeBaseName(to) ?? ''
   if (OFFICE_ROUTE_NAMES.has(baseName) && (!orgType || orgType === 'OT_GENERIC')) {
      return navigateTo(localePath(`/dashboard/${targetOrgId}`), { redirectCode: 302 })
   }

   return
})
